import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import { CalendarChallenge, challengeStageTypes, LookupObject } from '../../types/shared/Challenge';
import CalendarDayHeader from './CalendarDayHeader';
import CalendarGanttBars from './CalendarGanttBars';
import CalendarMonthHeader from './CalendarMonthHeader';
import CalendarRowDivider from './CalendarRowDivider';
import CalendarColDivider from './CalendarColDivider';
import CalendarSidebarItem from './CalendarSidebarItem';
import useChallengeCalendar from './_hooks/useChallengeCalendar';
import CalendarSpanBar from './CalendarSpanBar';
import dayjs from 'dayjs';
import { useMousePosition } from './_hooks/useMousePosition';

const Calendar: React.FC<CalendarProps> = ({
    challenges,
    startDate,
    endDate,
    selectedChallengeID,
    setSelectedChallengeID,
}) => {
    const calendarRef = useRef<HTMLDivElement>(null);
    const [scrollX, setScrollX] = useState<number>(0);
    const [scrollY, setScrollY] = useState<number>(0);
    const [tooltipContent, setTooltipContent] = useState<string[]>([]);
    const mouse = useMousePosition();
    const challengeArr = Object.values(challenges);
    const {
        monthHeaders,
        dayHeaders,
        formattedDateRange,
        selectedChallenge,
        buildGridRows,
        buildGridCols,
        buildGridAreas,
    } = useChallengeCalendar(challengeArr, startDate, endDate, selectedChallengeID);

    const contentStyles = {
        gridTemplateColumns: buildGridCols(formattedDateRange),
        gridTemplateRows: buildGridRows(challengeArr),
        gridTemplateAreas: buildGridAreas(challengeArr, formattedDateRange),
    };

    const scrollXStyles = {
        left: `${scrollX}px`,
    };
    const scrollYStyles = {
        top: `${scrollY}px`,
    };

    const lastChallenge = challengeArr[challengeArr.length - 1];

    const sideBarDividerStyles = {
        ...scrollXStyles,
        gridRowStart:
            selectedChallengeID === lastChallenge?.id
                ? `challenge-${lastChallenge?.id}-pitch-end`
                : `challenge-${lastChallenge?.id}-end`,
        gridRowEnd: 'rows-end',
    };

    function handleScroll() {
        if (!!calendarRef.current) {
            setScrollX(calendarRef.current.scrollLeft);
            setScrollY(calendarRef.current.scrollTop);
        }
    }

    const tooltipContainerStyles = {
        top: `${mouse.y}px`,
        left: `${mouse.x}px`,
    };

    return (
        <div className="calendar-modules-container">
            <div
                className="calendar-content"
                style={contentStyles}
                id="gantt-container"
                ref={calendarRef}
                onScroll={handleScroll}
            >
                {/* Dividing lines between rows */}
                {challengeArr.map((challenge: CalendarChallenge, i: number) => (
                    <CalendarRowDivider
                        key={i}
                        gridRow={`challenge-${challenge.id}-end`}
                        gridColumnStart={`sidebar-end`}
                        gridColumnEnd={`cols-end`}
                    />
                ))}
                {/* Extras for expanded rows */}
                {!!selectedChallengeID &&
                    !!challenges[selectedChallengeID] &&
                    challengeStageTypes.map((type: string, i: number) => (
                        <CalendarRowDivider
                            key={i}
                            gridRow={`challenge-${selectedChallenge.id}-${type}-end`}
                            gridColumnStart={`sidebar-end`}
                            gridColumnEnd={`cols-end`}
                        />
                    ))}
                {/* Dividing lines between columns */}
                {monthHeaders.slice(1).map((month: LookupObject, i: number) => {
                    const {
                        from: { y, m, d },
                    } = month;
                    return (
                        <CalendarColDivider
                            key={i}
                            gridColumn={`start-${y}-${m}-${d}`}
                            gridRowStart={`header-day-end`}
                            gridRowEnd={`rows-end`}
                        />
                    );
                })}
                {/* Extra Col divider at end */}
                <CalendarColDivider
                    gridColumn={`cols-end`}
                    gridRowStart={`header-day-end`}
                    gridRowEnd={`rows-end`}
                    color="#222222"
                />
                <div
                    className="calendar-sidebar-spacer"
                    style={{ ...scrollXStyles, ...scrollYStyles }}
                />
                {challengeArr.map((challenge: CalendarChallenge, i: number) => (
                    <CalendarSidebarItem
                        challenge={challenge}
                        key={i}
                        isExpanded={selectedChallengeID === challenge.id}
                        setSelectedChallengeID={setSelectedChallengeID}
                        index={i}
                        style={scrollXStyles}
                        setTooltipContent={setTooltipContent}
                    />
                ))}
                <div className="challenge" style={{ ...sideBarDividerStyles, zIndex: 0 }} />
                {/* Month & day headers */}
                {monthHeaders.map((month: LookupObject, i: number) => (
                    <CalendarMonthHeader
                        month={month}
                        key={i}
                        index={i}
                        length={monthHeaders.length}
                        style={scrollYStyles}
                    />
                ))}
                {dayHeaders.map((day: string, i: number) => {
                    const lastInMonth =
                        dayjs(day).format('MM') !== dayjs(day).add(1, 'day').format('MM') ||
                        i === dayHeaders.length - 1;
                    return (
                        <CalendarDayHeader
                            day={day}
                            key={i}
                            index={i}
                            lastInMonth={lastInMonth} // TODO
                            length={dayHeaders.length}
                            style={scrollYStyles}
                        />
                    );
                })}
                {/* Calendar Span Bars */}
                {challengeArr.map((challenge: CalendarChallenge, i: number) => (
                    <CalendarSpanBar
                        challenge={challenge}
                        key={i}
                        calendarStartDate={startDate}
                        calendarEndDate={endDate}
                        isExpanded={selectedChallengeID === challenge.id}
                    />
                ))}
                {/* Calendar Gantt bars */}
                {!!selectedChallengeID && !!challenges[selectedChallengeID] && (
                    <CalendarGanttBars
                        challenge={challenges[selectedChallengeID]}
                        calendarStartDate={startDate}
                        calendarEndDate={endDate}
                    />
                )}
                {!!tooltipContent.length && (
                    <div className="tooltip-container" style={tooltipContainerStyles}>
                        <div className="tooltip">
                            {tooltipContent.map((content: string, i: number) => (
                                <div key={i}>{content}</div>
                            ))}
                            <span className="tooltip-pointer" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Calendar;

interface CalendarProps {
    challenges: Record<number, CalendarChallenge>;
    startDate: Date;
    endDate: Date;
    selectedChallengeID: number | null;
    setSelectedChallengeID: Dispatch<SetStateAction<number | null>>;
}
